/**
 * The operator's view of SSH host-key pinning for one chassis.
 *
 * The card exists because the pinning has one deliberately unforgiving
 * behaviour — a changed host key refuses the session — and until now the only
 * way to see or undo that was the API. An operator at 2am meeting "the platform
 * cannot reach this OLT" needs to see which key is held, which one the chassis
 * is offering, and the way back in, on the page they are already on.
 *
 * Two things drive every decision in here:
 *
 *  - "Learned" is not "verified". A key taken on trust the first time we
 *    connected protects against the key CHANGING and proves nothing about the
 *    key itself. Showing a green tick for it would be a lie, so it is drawn as
 *    unfinished work with the fingerprint and the commands to finish it.
 *  - A fingerprint nobody can check is decoration. Every state that has one
 *    shows it in the OpenSSH form and the exact commands that produce the same
 *    string from somewhere other than this page.
 */
import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  KeyRound,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import {
  LEGACY_SSH_ALGORITHMS,
  type Olt,
  type OltHostKey,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import {
  Button,
  Card,
  ErrorNotice,
  Field,
  Input,
  Pill,
  Spinner,
  type PillTone,
} from '../../components/ui';

/**
 * OpenSSH's file/flag name for a key algorithm — `ssh-ed25519` is `ed25519` in
 * `ssh-keyscan -t` and in `/etc/ssh/ssh_host_ed25519_key.pub`. Returned as null
 * for anything unrecognised so the commands drop the flag rather than print a
 * wrong one.
 */
function sshKeyFamily(keyType: string | null | undefined): string | null {
  if (!keyType) return null;
  if (keyType.startsWith('ecdsa-')) return 'ecdsa';
  if (keyType === 'ssh-ed25519') return 'ed25519';
  if (keyType === 'ssh-rsa') return 'rsa';
  if (keyType === 'ssh-dss') return 'dsa';
  return null;
}

/**
 * Accepts a fingerprint the way it arrives from a terminal.
 *
 * `ssh-keygen -l` prints `256 SHA256:abc… host (ED25519)`, and an operator
 * copying the middle of that line gets either form. Base64 has no colon, so a
 * colon means the prefix (or another digest format) is already there and the
 * string is left exactly as typed.
 */
function normaliseFingerprint(input: string): string {
  const trimmed = input.trim().replace(/\s+/g, '');
  if (!trimmed || trimmed.includes(':')) return trimmed;
  return `SHA256:${trimmed}`;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      icon={copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line px-5 py-2.5">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm text-ink">{value}</span>
    </div>
  );
}

function FingerprintBlock({
  label,
  fingerprint,
  keyType,
  when,
  tone = 'neutral',
  copyable = true,
}: {
  label: string;
  fingerprint: string;
  keyType: string | null;
  when?: string;
  tone?: 'neutral' | 'danger';
  copyable?: boolean;
}) {
  return (
    <div className="px-5 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-faint">{label}</span>
        {when && <span className="text-xs text-faint">{when}</span>}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <code
          className={clsx(
            'flex-1 overflow-x-auto rounded-lg border px-3 py-2 font-mono text-xs',
            tone === 'danger'
              ? 'border-danger/40 bg-danger-soft text-danger'
              : 'border-line bg-surface-2 text-ink',
          )}
        >
          {fingerprint}
        </code>
        {copyable && <CopyButton value={fingerprint} />}
      </div>
      <p className="mt-1 text-xs text-muted">{keyType ?? 'unknown'} key</p>
    </div>
  );
}

/**
 * How to obtain the same string from somewhere that is not this page.
 *
 * Three commands, ordered by how much they actually prove. `known_hosts` is
 * first because a record written before today, on a machine the chassis has
 * never touched, is the strongest evidence most operators already have.
 */
function VerifyCommands({ olt, keyType }: { olt: Olt; keyType: string | null }) {
  const family = sshKeyFamily(keyType);
  const typeFlag = family ? ` -t ${family}` : '';
  // OpenSSH addresses a non-default port as `[host]:port`, in known_hosts and
  // on the ssh-keyscan command line alike.
  const hostToken = olt.cliPort === 22 ? olt.mgmtIp : `[${olt.mgmtIp}]:${olt.cliPort}`;
  const portFlag = olt.cliPort === 22 ? '' : ` -p ${olt.cliPort}`;

  const steps = [
    {
      how: 'If you have ever opened SSH to this OLT from your own machine, the answer is already on it — written before today, from a machine the chassis never touched.',
      cmd: `ssh-keygen -l -F ${hostToken}`,
    },
    {
      how: 'From a host on the management network:',
      cmd: `ssh-keyscan${typeFlag}${portFlag} ${olt.mgmtIp} | ssh-keygen -lf -`,
    },
    {
      how: family
        ? 'On the chassis itself, over the serial console — the one path nobody in the middle of the network is on. Where the firmware gives you a shell:'
        : 'On the chassis itself, over the serial console — the one path nobody in the middle of the network is on. Where the firmware gives you a shell, fingerprint its own host key file:',
      cmd: `ssh-keygen -lf /etc/ssh/ssh_host_${family ?? '<type>'}_key.pub`,
    },
  ];

  return (
    <div className="space-y-3 border-t border-line px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-faint">
        How to check this fingerprint
      </p>
      {steps.map((step) => (
        <div key={step.cmd}>
          <p className="text-xs text-muted">{step.how}</p>
          <div className="mt-1 flex gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-xs text-ink">
              {step.cmd}
            </code>
            <CopyButton value={step.cmd} />
          </div>
        </div>
      ))}
      <p className="text-xs text-faint">
        ssh-keyscan reads the key over the same management path the platform uses, so it catches a
        stale or wrong pin but cannot by itself rule out something sitting on that path. The console
        is the check that can.
      </p>
    </div>
  );
}

/** The five states, as a headline the operator reads first. */
const STATE_LABEL: Record<OltHostKey['state'], string> = {
  pinned: 'verified',
  learned: 'not verified',
  mismatch: 'key changed',
  unpinned: 'no pin yet',
  telnet: 'telnet',
};

const STATE_TONE: Record<OltHostKey['state'], PillTone> = {
  pinned: 'success',
  learned: 'warn',
  mismatch: 'danger',
  unpinned: 'neutral',
  telnet: 'warn',
};

type Pending =
  | { kind: 'confirm' }
  | { kind: 'accept' }
  | { kind: 'forget' }
  | { kind: 'warn' }
  | { kind: 'legacy-off' };

export function OltHostKeyCard({ olt }: { olt: Olt }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const canManage = can('olt.manage');
  const tasks = useTasks();
  const [pending, setPending] = useState<Pending | null>(null);
  const [showRisky, setShowRisky] = useState(false);

  const queryKey = ['olt', olt.id, 'host-key'];
  /**
   * Seeded from the OLT DTO, which already carries this, so the card renders
   * with the page instead of flashing empty. Every write below returns the
   * fresh state, so the answer is put straight into the cache rather than
   * invalidating and re-reading a live chassis for the sake of one flag.
   */
  const hostKeyQuery = useQuery({
    queryKey,
    queryFn: () => api.get<OltHostKey>(`/olts/${encodeURIComponent(olt.id)}/host-key`),
    initialData: olt.hostKey,
    refetchInterval: 30_000,
  });
  const hostKey = hostKeyQuery.data;

  /**
   * Every write on this card goes through the tasks tray: the dialog that
   * asked for it (if any) is already closed by the time the API answers, and
   * the toast carries the sentence. The fresh state the API returns is put
   * straight into the cache, as before, so the card re-draws without another
   * round trip.
   */
  function act(label: string, run: () => Promise<OltHostKey>, done: string) {
    setPending(null);
    tasks.start<OltHostKey>({
      label,
      href: `/olts/${encodeURIComponent(olt.id)}`,
      run: async () => {
        const fresh = await run();
        queryClient.setQueryData(queryKey, fresh);
        return fresh;
      },
      done: () => done,
    });
  }

  function open(next: Pending) {
    setPending(next);
  }

  const base = `/olts/${encodeURIComponent(olt.id)}/host-key`;

  function accept(fingerprint: string) {
    act(
      `Accept host key — ${olt.name}`,
      () => api.post<OltHostKey>(`${base}/accept`, { fingerprint }),
      `${olt.name} is re-pinned to the key it is presenting. Sessions to it work again.`,
    );
  }
  function confirm(fingerprint: string) {
    act(
      `Confirm host key — ${olt.name}`,
      () => api.post<OltHostKey>(`${base}/confirm`, { fingerprint }),
      `${olt.name}'s host key is recorded as checked against the chassis.`,
    );
  }
  function forget() {
    act(
      `Forget host key — ${olt.name}`,
      () => api.delete<OltHostKey>(base),
      `The pin for ${olt.name} is gone. The next session records whatever answers.`,
    );
  }
  function setPolicy(policy: 'enforce' | 'warn') {
    act(
      `Host-key policy ${policy} — ${olt.name}`,
      () => api.patch<OltHostKey>(base, { policy }),
      policy === 'enforce'
        ? `${olt.name} refuses a changed host key again.`
        : `${olt.name} no longer refuses a changed host key. Set it back to enforce when you are done.`,
    );
  }
  function setLegacy(enabled: boolean) {
    act(
      `Legacy SSH algorithms ${enabled ? 'on' : 'off'} — ${olt.name}`,
      () => api.patch<OltHostKey>(base, { legacyAlgorithms: enabled }),
      enabled
        ? `${olt.name} is offered the legacy algorithms again.`
        : `${olt.name} is no longer offered the legacy algorithms.`,
    );
  }

  if (!hostKey) {
    return (
      <Card title="SSH host key">
        <div className="flex justify-center p-8">
          {hostKeyQuery.isError ? <ErrorNotice error={hostKeyQuery.error} /> : <Spinner />}
        </div>
      </Card>
    );
  }

  const { state, fingerprint, keyType, pendingFingerprint, pendingKeyType, policy, negotiated } =
    hostKey;
  const legacyUsed = negotiated
    ? [negotiated.kex, negotiated.cipher, negotiated.serverHostKey].filter((algorithm) =>
        LEGACY_SSH_ALGORITHMS.includes(algorithm),
      )
    : [];

  const verdict: Record<OltHostKey['state'], string> = {
    pinned: `A person compared this key against ${olt.name} and confirmed it. If the chassis ever presents a different one, the session is refused.`,
    learned: `Recorded the first time SimpleACS connected — nobody has compared it against ${olt.name} itself. It protects you from the key changing from that moment on; it is not evidence that this is the right key.`,
    mismatch:
      policy === 'enforce'
        ? `${olt.name} is presenting a different SSH host key. Every CLI session to it is being refused right now, and no password has been sent to whatever is answering.`
        : `${olt.name} is presenting a different SSH host key. This OLT's policy is "warn", so sessions are still going through and the CLI password is still being sent to whatever answers.`,
    unpinned: `Nothing pinned yet. The next successful CLI session records the key ${olt.name} presents, and from then on a different key is refused. Until then a key change would pass unnoticed.`,
    telnet: `${olt.name} is managed over telnet, which has no host key to pin. The CLI password crosses the management network in clear text on every session, readable by anything on the path.`,
  };

  const primaryAction =
    !canManage ? null : state === 'mismatch' ? (
      <Button
        size="sm"
        variant="danger"
        icon={<ShieldAlert className="size-3.5" />}
        onClick={() => open({ kind: 'accept' })}
      >
        Accept the new key
      </Button>
    ) : state === 'learned' ? (
      <Button
        size="sm"
        variant="primary"
        icon={<ShieldCheck className="size-3.5" />}
        onClick={() => open({ kind: 'confirm' })}
      >
        Confirm it
      </Button>
    ) : null;

  return (
    <>
      <Card
        title={
          <span className="flex items-center gap-2">
            <KeyRound className="size-4 text-muted" />
            SSH host key
            <Pill tone={STATE_TONE[state]}>{STATE_LABEL[state]}</Pill>
          </span>
        }
        action={primaryAction}
      >
        <div
          className={clsx(
            'border-b border-line px-5 py-3 text-sm',
            state === 'mismatch'
              ? 'bg-danger-soft text-danger'
              : state === 'pinned'
                ? 'text-success'
                : state === 'unpinned'
                  ? 'text-muted'
                  : 'text-warn',
          )}
        >
          {verdict[state]}
        </div>

        {state === 'telnet' ? (
          <p className="px-5 py-3 text-sm text-muted">
            Switch this OLT's CLI transport to SSH under Edit, and pinning starts on its first
            session. Nothing here can protect a telnet session — the fix is the transport.
          </p>
        ) : state === 'unpinned' ? (
          <>
            <p className="px-5 py-3 text-sm text-muted">
              Test connection at the top of this page opens a CLI session, which is enough to learn
              a key. Once it appears, check it against the chassis and confirm it — a learned key
              that nobody confirms stays a guess about which machine answered first.
            </p>
            <PolicyRow
              policy={policy}
              oltName={olt.name}
              canManage={canManage}
              onEnforce={() => setPolicy('enforce')}
            />
            <LegacyRow
              hostKey={hostKey}
              oltName={olt.name}
              legacyUsed={legacyUsed}
              canManage={canManage}
              onTurnOn={() => setLegacy(true)}
              onTurnOffSafely={() => setLegacy(false)}
              onTurnOffRisky={() => open({ kind: 'legacy-off' })}
            />
          </>
        ) : state === 'mismatch' && pendingFingerprint ? (
          <>
            <div className="grid gap-0 border-b border-line sm:grid-cols-2 sm:divide-x sm:divide-line">
              <FingerprintBlock
                label="Pinned"
                fingerprint={fingerprint ?? '—'}
                keyType={keyType}
                when={`recorded ${formatRelative(hostKey.learnedAt)}`}
              />
              <FingerprintBlock
                label="Presented now"
                fingerprint={pendingFingerprint}
                keyType={pendingKeyType}
                when={`first seen ${formatRelative(hostKey.pendingAt)}`}
                tone="danger"
              />
            </div>
            <p className="border-b border-line px-5 py-3 text-sm text-muted">
              Two things cause this. A firmware upgrade regenerates a chassis's host key — the
              ordinary reason, and the likely one if {olt.name} was upgraded recently. Or something
              on the management path is answering on {olt.mgmtIp} instead of {olt.name}, and
              accepting its key hands it the CLI password on the next session. Read the fingerprint
              off the chassis over a path you already trust before you accept anything.
            </p>
            <VerifyCommands olt={olt} keyType={pendingKeyType} />
            <PolicyRow
              policy={policy}
              oltName={olt.name}
              canManage={canManage}
              onEnforce={() => setPolicy('enforce')}
            />
            <LegacyRow
              hostKey={hostKey}
              oltName={olt.name}
              legacyUsed={legacyUsed}
              canManage={canManage}
              onTurnOn={() => setLegacy(true)}
              onTurnOffSafely={() => setLegacy(false)}
              onTurnOffRisky={() => open({ kind: 'legacy-off' })}
            />
          </>
        ) : (
          <>
            <div className="border-b border-line">
              <FingerprintBlock
                label={state === 'pinned' ? 'Pinned and checked' : 'Pinned, unchecked'}
                fingerprint={fingerprint ?? '—'}
                keyType={keyType}
              />
            </div>
            <Row label="Recorded" value={formatRelative(hostKey.learnedAt)} />
            <Row
              label="Last presented"
              value={
                hostKey.lastSeenAt
                  ? formatRelative(hostKey.lastSeenAt)
                  : 'no session since this key was accepted'
              }
            />
            {state === 'learned' && <VerifyCommands olt={olt} keyType={keyType} />}
            <PolicyRow
              policy={policy}
              oltName={olt.name}
              canManage={canManage}
              onEnforce={() => setPolicy('enforce')}
            />
            <LegacyRow
              hostKey={hostKey}
              oltName={olt.name}
              legacyUsed={legacyUsed}
              canManage={canManage}
              onTurnOn={() => setLegacy(true)}
              onTurnOffSafely={() => setLegacy(false)}
              onTurnOffRisky={() => open({ kind: 'legacy-off' })}
            />
          </>
        )}

        {/*
          Folded away on purpose. Both of these make this OLT less protected than
          it is now, and neither should sit one mis-click from Confirm.
        */}
        {canManage && state !== 'telnet' && (policy === 'enforce' || fingerprint) && (
          <div className="border-t border-line px-5 py-3">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs text-faint hover:text-ink"
              onClick={() => setShowRisky((value) => !value)}
            >
              {showRisky ? (
                <ChevronDown className="size-3.5" />
              ) : (
                <ChevronRight className="size-3.5" />
              )}
              Actions that reduce protection
            </button>
            {showRisky && (
              <div className="mt-3 space-y-3">
                {policy === 'enforce' && (
                  <RiskyAction
                    title="Stop refusing a changed key"
                    body={`On "warn", ${olt.name} presenting a different host key no longer stops the session — it is recorded and shown here, and the CLI password goes to whatever answered. It is a way back into a chassis you cannot reach any other way, not somewhere to leave an OLT.`}
                    button="Set policy to warn"
                    onClick={() => open({ kind: 'warn' })}
                  />
                )}
                {fingerprint && (
                  <RiskyAction
                    title="Forget the pin and learn again"
                    body={`Drops the key held for ${olt.name}. The next session trusts whatever answers on ${olt.mgmtIp} and pins that instead. If something is on the path right now, that is what gets pinned.${
                      state === 'mismatch'
                        ? ' Accepting the presented key is almost always the better move — it names the fingerprint, so it cannot pin something you did not read.'
                        : ''
                    }`}
                    button="Forget the pin"
                    onClick={() => open({ kind: 'forget' })}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {pending?.kind === 'accept' && pendingFingerprint && (
        <FingerprintDialog
          title={`Accept the key ${olt.name} is presenting`}
          subtitle={`Re-pins ${olt.name} to this key. Sessions to it start working again immediately.`}
          warning={`Only do this once you have read the fingerprint off the chassis. Accepting a key you have not checked pins whatever is answering on ${olt.mgmtIp}, and the next session sends it the CLI password.`}
          shows={[
            { label: 'Pinned until now', value: fingerprint ?? '—', keyType, tone: 'neutral' },
            {
              label: 'Being presented',
              value: pendingFingerprint,
              keyType: pendingKeyType,
              tone: 'danger',
            },
          ]}
          fieldLabel="Type the fingerprint the chassis is presenting"
          fieldHint='Paste it from your terminal, not from this page — that is the whole check. The "SHA256:" prefix is optional.'
          expected={pendingFingerprint}
          confirmLabel="Accept"
          confirmVariant="danger"
          onConfirm={accept}
          onClose={() => setPending(null)}
        />
      )}

      {pending?.kind === 'confirm' && fingerprint && (
        <FingerprintDialog
          title={`Confirm ${olt.name}'s host key`}
          subtitle="Records that a person compared this key against the chassis."
          warning={`Confirming is the only thing that turns a learned key into a checked one. It says you read this fingerprint off ${olt.name} itself and it matched — so do not confirm one you have not looked at.`}
          shows={[{ label: 'Pinned', value: fingerprint, keyType, tone: 'neutral' }]}
          fieldLabel="Type the fingerprint you read on the chassis"
          fieldHint='It has to match the pinned key. If it does not, stop — that is a mismatch worth investigating, not confirming. The "SHA256:" prefix is optional.'
          expected={fingerprint}
          confirmLabel="Confirm"
          confirmVariant="primary"
          onConfirm={confirm}
          onClose={() => setPending(null)}
        />
      )}

      {pending?.kind === 'forget' && (
        <NameDialog
          title={`Forget ${olt.name}'s host key`}
          subtitle="The next connection trusts whatever answers."
          oltName={olt.name}
          warning={`This drops the pin entirely. The next CLI session accepts whatever key answers on ${olt.mgmtIp} and records it as ${olt.name}'s, with nothing to compare it against. If something is sitting on that path right now, it is what gets pinned.`}
          confirmLabel="Forget"
          onConfirm={forget}
          onClose={() => setPending(null)}
        />
      )}

      {pending?.kind === 'warn' && (
        <NameDialog
          title={`Set ${olt.name} to warn`}
          subtitle="A changed host key stops being refused."
          oltName={olt.name}
          warning={`On "warn", a chassis presenting a different key still gets the session and still gets the CLI password. The change is recorded and shown on this card, but nothing is stopped. Use it to get back into a chassis you cannot reach any other way, then set it back to enforce.`}
          confirmLabel="Apply"
          onConfirm={() => setPolicy('warn')}
          onClose={() => setPending(null)}
        />
      )}

      {pending?.kind === 'legacy-off' && (
        <NameDialog
          title={`Stop offering legacy algorithms to ${olt.name}`}
          subtitle="This one can cost you SSH to the chassis."
          oltName={olt.name}
          warning={
            negotiated
              ? `${olt.name} negotiated ${legacyUsed.join(', ')} on its last connection. The platform already offers the modern algorithms first, so that is the best this chassis can do — take the legacy set away and SSH to it stops working until its firmware is upgraded.`
              : `Nothing has been recorded about what ${olt.name} can negotiate — it has not completed an SSH handshake since this shipped. Turning the legacy set off now is a guess, and the cost of guessing wrong is no SSH to a live OLT. Let it connect once and read what it agreed to first.`
          }
          confirmLabel="Apply"
          onConfirm={() => setLegacy(false)}
          onClose={() => setPending(null)}
        />
      )}
    </>
  );
}

function PolicyRow({
  policy,
  oltName,
  canManage,
  onEnforce,
}: {
  policy: OltHostKey['policy'];
  oltName: string;
  canManage: boolean;
  onEnforce: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-3">
      <div className="min-w-0">
        <span className="text-sm text-ink">
          Policy: {policy === 'enforce' ? 'enforce' : 'warn'}
        </span>
        <p className={clsx('text-xs', policy === 'enforce' ? 'text-muted' : 'text-danger')}>
          {policy === 'enforce'
            ? 'A different host key refuses the session and no password is sent.'
            : `A different host key is recorded and ${oltName} gets the session anyway, password and all.`}
        </p>
      </div>
      {canManage && policy === 'warn' && (
        <Button size="sm" variant="success" onClick={onEnforce}>
          Back to enforce
        </Button>
      )}
    </div>
  );
}

/**
 * The legacy set, with what the chassis actually agreed to beside it.
 *
 * Printed rather than summarised because "can I turn this off?" has an exact
 * answer and it is in these three strings. The platform offers the modern
 * algorithms first, so whatever was negotiated is the best this chassis can do.
 */
function LegacyRow({
  hostKey,
  oltName,
  legacyUsed,
  canManage,
  onTurnOn,
  onTurnOffSafely,
  onTurnOffRisky,
}: {
  hostKey: OltHostKey;
  oltName: string;
  legacyUsed: string[];
  canManage: boolean;
  onTurnOn: () => void;
  onTurnOffSafely: () => void;
  onTurnOffRisky: () => void;
}) {
  const { negotiated, legacyAlgorithms, legacyInUse } = hostKey;
  const safeToTurnOff = negotiated !== null && !legacyInUse;

  // The guidance has to answer the question the operator is actually holding,
  // and that question flips with the switch: "can I turn this off?" while it is
  // on, "is this why SSH broke?" once it is off.
  const guidance = legacyAlgorithms
    ? !negotiated
      ? `Nothing recorded — ${oltName} has not completed an SSH handshake since this shipped. Turning the legacy set off now is a guess, and guessing wrong costs SSH to a live OLT. Let it connect once first.`
      : legacyUsed.length > 0
        ? `${oltName} used ${legacyUsed.join(' and ')} last time. The platform offers the modern algorithms first, so that is the best this chassis can do — turning the legacy set off takes SSH to it away until its firmware is upgraded.`
        : `${oltName} agreed to none of the legacy algorithms last time. The platform offers the modern ones first, so this is already what it is using — turning the legacy set off changes nothing on the wire.`
    : !negotiated
      ? `Nothing recorded — ${oltName} has not completed an SSH handshake since the legacy set was taken away from it. If its firmware is old enough to need one of them, SSH to it will keep failing until they go back on.`
      : legacyUsed.length > 0
        ? `${oltName} used ${legacyUsed.join(' and ')} the last time it connected, and is no longer offered them. If SSH to it has stopped working, this is why.`
        : `${oltName} agreed to none of the legacy algorithms the last time it connected, so it does not need them. Leave this off.`;

  return (
    <div className="border-b border-line px-5 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <span className="text-sm text-ink">Legacy SSH algorithms</span>
          <p className="text-xs text-muted">
            {legacyAlgorithms
              ? 'Offered to this OLT: the SHA-1 key exchanges, the CBC ciphers and ssh-rsa.'
              : 'Not offered to this OLT.'}
          </p>
        </div>
        {canManage &&
          (legacyAlgorithms ? (
            <Button
              size="sm"
              variant={safeToTurnOff ? 'success' : 'secondary'}
              onClick={safeToTurnOff ? onTurnOffSafely : onTurnOffRisky}
              title={
                safeToTurnOff
                  ? 'Safe here — this chassis negotiated none of them.'
                  : 'Needs confirming — this could take SSH to the chassis away.'
              }
            >
              {safeToTurnOff ? 'Turn off' : 'Turn off…'}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onTurnOn}
              title="Offers the older key exchanges, ciphers and SHA-1 signatures to this chassis again."
            >
              Turn on
            </Button>
          ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-faint">Last negotiated:</span>
        {negotiated ? (
          [negotiated.kex, negotiated.cipher, negotiated.serverHostKey].map((algorithm, index) => (
            <Pill state
              key={`${index}-${algorithm}`}
              tone={LEGACY_SSH_ALGORITHMS.includes(algorithm) ? 'warn' : 'neutral'}
            >
              <span className="font-mono">{algorithm}</span>
            </Pill>
          ))
        ) : (
          <span className="text-xs text-faint">nothing recorded yet</span>
        )}
      </div>
      <p className={clsx('mt-2 text-xs', legacyUsed.length > 0 || !negotiated ? 'text-warn' : 'text-success')}>
        {guidance}
      </p>
    </div>
  );
}

function RiskyAction({
  title,
  body,
  button,
  onClick,
}: {
  title: string;
  body: string;
  button: string;
  onClick: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-line bg-surface-2 px-3 py-2.5">
      <div className="min-w-0">
        <span className="text-sm text-ink">{title}</span>
        <p className="mt-0.5 text-xs text-muted">{body}</p>
      </div>
      <Button size="sm" variant="danger" className="shrink-0" onClick={onClick}>
        {button}
      </Button>
    </div>
  );
}

// --- dialogs ------------------------------------------------------------------

/**
 * Accept and confirm both make the operator produce the fingerprint rather than
 * click a button, which is exactly what the API demands of them — and the point
 * is that they produce it from the chassis, not from this screen.
 *
 * Confirm CLOSES the dialog; the write itself runs in the tasks tray, so there
 * is no spinner or error inside it — the tray keeps a refused write on screen
 * until it is dismissed.
 */
function FingerprintDialog({
  title,
  subtitle,
  warning,
  shows,
  fieldLabel,
  fieldHint,
  expected,
  confirmLabel,
  confirmVariant,
  onConfirm,
  onClose,
}: {
  title: string;
  subtitle: string;
  warning: string;
  shows: Array<{ label: string; value: string; keyType: string | null; tone: 'neutral' | 'danger' }>;
  fieldLabel: string;
  fieldHint: string;
  expected: string;
  confirmLabel: string;
  confirmVariant: 'primary' | 'danger';
  onConfirm: (fingerprint: string) => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState('');
  const normalised = normaliseFingerprint(typed);
  const armed = normalised === expected;
  const mistyped = normalised.length >= expected.length && !armed;

  return (
    <WizardDialog
      title={title}
      subtitle={subtitle}
      maxWidth="2xl"
      bodyClassName="space-y-4 p-5"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant={confirmVariant} disabled={!armed} onClick={() => onConfirm(normalised)}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <p className="rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-sm text-warn">
        {warning}
      </p>
      <div className="divide-y divide-line rounded-lg border border-line">
        {shows.map((entry) => (
          <FingerprintBlock
            key={entry.label}
            label={entry.label}
            fingerprint={entry.value}
            keyType={entry.keyType}
            tone={entry.tone}
            copyable={false}
          />
        ))}
      </div>
      <Field
        label={fieldLabel}
        hint={fieldHint}
        error={mistyped ? 'That is not the fingerprint on this page. Check it again before going on.' : undefined}
      >
        <Input
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          placeholder="SHA256:…"
          className="font-mono"
          autoFocus
        />
      </Field>
    </WizardDialog>
  );
}

/**
 * Type the OLT's name — the page's existing friction for a change it cannot
 * undo for you. Confirm closes it and the write runs in the tasks tray.
 */
function NameDialog({
  title,
  subtitle,
  oltName,
  warning,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  title: string;
  subtitle: string;
  oltName: string;
  warning: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState('');
  return (
    <WizardDialog
      title={title}
      subtitle={subtitle}
      maxWidth="2xl"
      bodyClassName="space-y-4 p-5"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={typed.trim() !== oltName} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <p className="rounded-lg border border-danger/40 bg-danger-soft px-3 py-2 text-sm text-danger">
        {warning}
      </p>
      <Field label={`Type "${oltName}" to confirm`}>
        <Input
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          placeholder={oltName}
          autoFocus
        />
      </Field>
    </WizardDialog>
  );
}
